import { type SubmitEvent as ReactSubmitEvent, memo, useEffect, useState } from "react";
import { loginUserAction } from "Data/Redux/Slices/User";
import { selectCurrentUser, selectToken } from "Data/Redux/Slices/User";
import "Components/LoginPage/LoginPage.less";
import { useAppSelector, useAppDispatch } from "Data/Redux/Store";
import { useNavigate } from "react-router-dom";
import type { LoginPayload } from "Interfaces";
import { NotificationType, notify } from "src/Notifications";
import { Button, ButtonTypes, Input } from "@clintonelec/react-storybook";
import AuthCard from "Components/Global/AuthCard";
import { selectNeedsPassword } from "Data/Redux/Slices/UserSettings";
import NewPasswordPage from "Components/NewPasswordPage";

type LoginForm = {
	username: HTMLInputElement;
	password: HTMLInputElement;
} & HTMLFormElement;

function LoginPage() {
	const dispatch = useAppDispatch();
	const navigate = useNavigate();
	const authToken = useAppSelector(selectToken);
	const currentUser = useAppSelector(selectCurrentUser);
	const loginUser = (payload: LoginPayload) => dispatch(loginUserAction(payload));
	const [ submitted, setSubmitted ] = useState(false);
	const needsPassword = useAppSelector(selectNeedsPassword);

	useEffect(() => {
		if (authToken && currentUser) {
			navigate("/");
		}
	}, [ authToken, currentUser, navigate, needsPassword ]);

	if (needsPassword) {
		return (
			<NewPasswordPage />
		);
	}

	const handleLoginSubmit = (event: ReactSubmitEvent<LoginForm>) => {
		event.preventDefault();
		setSubmitted(true);

		const { username, password } = event.currentTarget;
		const payload: LoginPayload = {
			password: password.value,
			username: username.value
		};

		if (event.currentTarget?.checkValidity()) {
			loginUser(payload)
				.unwrap()
				.catch(() => {
					notify(NotificationType.ERROR, "There was an error logging you in.");
				});
		}
	};

	return (
		<div className="login-page">
			<AuthCard onSubmit={ handleLoginSubmit }>
				<label>Username</label>
				<Input
					name="username"
					noValidate={ !submitted }
					validityMessage="A username is required"
					wrapClassName="username-input"
				/>
				<label>Password</label>
				<Input
					name="password"
					required
					password
					noValidate={ !submitted }
					validityMessage="Password is required"
				/>
				<Button
					className="login-button"
					type={ ButtonTypes.PRIMARY }
					htmlType="submit"
				>
					Sign In
				</Button>
			</AuthCard>
		</div>
	);
}

export default memo(LoginPage);
